import { Select, Tooltip } from "antd";
import * as React from "react";
import { Button } from "src/components/buttons";
import { DataTable } from "src/components/common";
import { ActionIcon } from "src/components/forms/ActionColumnIconsAndButton";
import { Icon } from "src/components/icons/Icon";
import { House, Lock } from "lucide-react";
import NeurapulseUnlinkModal from "../neurapulse/NeurapulseUnlinkModal";

const { Option } = Select;

interface IProps {
	client: ClientDTO;
	datasets: DatasetDTO[];
	disabled?: boolean;
	onClientDatasetsAdd?: any;
	onClientDatasetsDelete?: any;
	onNeurapulseUnlink: () => void;
}

interface ISelectedDataset {
	clientId: number;
	datasetId: number;
	datasetName: string;
	homeDataset: boolean;
	neurapulseProjectEntryId: number;
}

interface IState {
	selectedDatasetId: number;
	selectedClientDatasets: ClientXrefDatasetsDTO[];
	selectedDatasets: ISelectedDataset[];
	showNeurapulseColumn: boolean;
	showUnlinkConfirmationModal: boolean;
	selectedLinkedDatasetId: number;
}

class ClientDatasetSelector extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			selectedDatasetId: -1,
			selectedClientDatasets: [],
			selectedDatasets: [],
			showNeurapulseColumn: false,
			showUnlinkConfirmationModal: false,
			selectedLinkedDatasetId: 0,
		};
	}

	public componentDidMount() {
		this.loadGrid();
	}

	public componentDidUpdate(
		prevProps: Readonly<IProps>,
		prevState: Readonly<IState>,
		snapshot?: any
	): void {
		if (prevProps.client !== this.props.client) {
			this.loadGrid();
		}
	}

	public loadGrid() {
		if (this.props.client.clientDatasets) {
			const hasNeurapulse = this.props.client.clientDatasets.some((d) =>
				d.neurapulseProjectEntryId! > 0 ? true : false
			);
			if (hasNeurapulse) {
				this.setState({ showNeurapulseColumn: true });
			}

			const selectedDatasets = this.props.client.clientDatasets.map((cd) => {
				const dataset = this.props.datasets.find((d) => d.id === cd.datasetId);
				if (dataset) {
					return {
						clientId: this.props.client.id!,
						datasetId: dataset.id!,
						datasetName: dataset.name!,
						homeDataset:
							this.props.client.datasetId === dataset.id! ? true : false,
						hasAssessments: cd.hasAssessments,
						neurapulseProjectEntryId: cd.neurapulseProjectEntryId || 0,
					};
				} else {
					return {
						clientId: this.props.client.id!,
						datasetId: -1,
						datasetName: "Invalid Dataset",
						homeDataset: false,
						hasAssessments: false,
						neurapulseProjectEntryId: 0,
					};
				}
			});
			if (selectedDatasets) {
				this.setState({ selectedDatasets });
			}
		}
	}

	private onNeurapulseUnlinkClick = async () => {
		this.props.onNeurapulseUnlink();
		this.loadGrid();
		this.setState({ showUnlinkConfirmationModal: false });
	};

	private unlinkNeurapulseModal = () => {
		return (
			<NeurapulseUnlinkModal
				organizationId={this.props.client.organizationId!}
				datasetId={this.state.selectedLinkedDatasetId}
				clientId={this.props.client.id!}
				visible={this.state.showUnlinkConfirmationModal}
				onConfirm={this.onNeurapulseUnlinkClick}
				onCancel={() => this.setState({ showUnlinkConfirmationModal: false })}
			/>
		);
	};

	public onChange(selectedClientDatasets: ClientXrefDatasetsDTO[]) {
		this.props.onClientDatasetsAdd(selectedClientDatasets);
		this.loadGrid();
	}

	public onDelete(selectedDataset: ISelectedDataset) {
		this.props.onClientDatasetsDelete(selectedDataset.datasetId);
		this.loadGrid();
	}

	public getColumns() {
		const columns = [
			{
				accessor: "datasetId",
				show: false,
			},
			{
				Header: <Tooltip title="Dataset">Dataset</Tooltip>,
				accessor: "datasetName",
			},
			{
				Header: <Tooltip title="Nuerapulse">Neurapulse</Tooltip>,
				accessor: "neurapulseLinked",
				show: this.state.showNeurapulseColumn,
				Cell: (rowInfo: any) => {
					if (rowInfo.original.neurapulseProjectEntryId > 0) {
						return (
							<Button
								className="btn-primary mr-3"
								type="button"
								onClick={() => {
									this.setState({
										showUnlinkConfirmationModal: true,
										selectedLinkedDatasetId: rowInfo.original.datasetId,
									});
								}}
							>
								Unlink Neurapulse
							</Button>
						);
					} else {
						return "";
					}
				},
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: (rowInfo: any) => {
					return (
						<>
							{rowInfo.original.homeDataset === false ? (
								rowInfo.original.hasAssessments !== true ? (
									<ActionIcon
										title="Delete"
										type="delete"
										onClick={() => this.onDelete(rowInfo.original)}
									/>
								) : (
									<></>
								)
							) : (
								<Tooltip title="Home Dataset" className={"form-icon"}>
									<Icon icon={House} />
								</Tooltip>
							)}
							{rowInfo.original.hasAssessments === true ? (
								<Tooltip title="Has Sessions" className={"form-icon"}>
									<Icon icon={Lock} />
								</Tooltip>
							) : (
								<></>
							)}
						</>
					);
				},
				id: "Actions",
			},
		];
		return columns;
	}

	public renderDatasetSelect() {
		const selectableDatasets = this.props.datasets.map((d) => {
			if (
				!this.props.client.clientDatasets!.find((dx) => dx.datasetId === d.id)
			) {
				return d;
			} else {
				return;
			}
		});

		return (
			<Select
				dropdownMatchSelectWidth={true}
				onChange={(v: number) => {
					this.setState({ selectedDatasetId: v });
				}}
				disabled={this.props.disabled}
				defaultValue={-1}
				value={this.state.selectedDatasetId}
			>
				<Option key={"datasetSelection_-1"} value={-1}></Option>
				{selectableDatasets.map(
					(d) =>
						d && (
							<Option key={"datasetSelection_" + d.id} value={d.id}>
								{d.name}
							</Option>
						)
				)}
			</Select>
		);
	}

	public render() {
		const columns = this.getColumns();
		return (
			<div className="container-fluid">
				<div className="row">
					<div className="col form-group">
						<DataTable data={this.state.selectedDatasets} columns={columns} />
					</div>
				</div>
				<div className="row">
					<div className="col-3 form-group">{this.renderDatasetSelect()}</div>
					<div className="col-3 form-group">
						<Button
							className="btn-primary mr-3"
							onClick={() => {
								if (this.state.selectedDatasetId > 0) {
									let selectedDatasets: ClientXrefDatasetsDTO[];
									if (this.props.client.clientDatasets) {
										selectedDatasets = [...this.props.client.clientDatasets];
										selectedDatasets.push({
											clientId: this.props.client.id,
											datasetId: this.state.selectedDatasetId,
										});
									} else {
										selectedDatasets = [
											{
												clientId: this.props.client.id,
												datasetId: this.state.selectedDatasetId,
											},
										];
									}
									this.setState({
										selectedClientDatasets: selectedDatasets,
										selectedDatasetId: -1,
									});
									this.onChange(selectedDatasets);
								}
							}}
							disabled={this.props.disabled}
						>
							Add
						</Button>
					</div>
				</div>
				{this.state.showNeurapulseColumn && this.unlinkNeurapulseModal()}
			</div>
		);
	}
}

export default ClientDatasetSelector;
